import React, { useState, useRef, useEffect } from "react";
import { Plus, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { usePlanLimit } from "../../hooks/usePlanLimit";
import PlanUsage, { PLAN_FULL_TITLE, usePlanUsage } from "../../components/PlanUsage";
import { useSelector } from "react-redux";
import FleetrifyCard from "../../components/Card";
import DashboardLayout from "../../components/DashboardLayout";
import { useDashboardControllers } from "../../controllers/useDashboardControllers";
import { DashboardFleetSummaryCard } from "../../components/pages/Dashboard/Cards/summary";
import BranchTable from "../../components/Table/BranchTable";
import BranchUpload from "../../components/pages/Users/modal/BranchUpload";
import BranchBulkUpload from "../../components/pages/Users/modal/BranchBulkUpload";
import { useBranchesController } from "../../controllers/useBranchesController";
import {
    useDeactivateBranchMutation,
    useReactivateBranchMutation,
} from "../../redux/services/branchApi";
import { usePermission } from "../../utils/permissions";
import { selectIsAdmin } from "../../redux/slices/authSlice";
import { useSingleUserController } from "../../controllers/useSingleUserController";
import { useGetRolePermissionsQuery } from "../../redux/services/staffApi";

const Branch = () => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [view, setView] = useState<"overview" | "manualUpload">("overview");
    const [currentPage, setCurrentPage] = useState(1);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const pageSize = 10;

    const { dateRange, handleDateRangeChange } = useDashboardControllers();
    const { can } = usePermission();
    const isAdmin = useSelector(selectIsAdmin);

    const {
        branches,
        totalBranches,
        activeCount,
        inActiveCount,
        isBranchesLoading,
        createBranch,
        updateBranch,
        deleteBranch,
        refetchBranches,
        showBulkUploadModal,
        setShowBulkUploadModal,
        totalPages,
    } = useBranchesController(undefined, currentPage, pageSize);

    const [deactivateBranch] = useDeactivateBranchMutation();
    const [reactivateBranch] = useReactivateBranchMutation();

    const { userDetails } = useSingleUserController();
          const getRoleName = (roleData: any): string => {
  if (typeof roleData === "object" && roleData?.name) {
    return roleData.name;
  }
  return typeof roleData === "string" ? roleData : "Role";
};

const getRoleId = (roleData: any): string | null => {
  if (typeof roleData === "object" && roleData?.id) {
    return roleData.id;
  }
  return null;
};

const roleName = getRoleName(userDetails?.role);
const roleId = getRoleId(userDetails?.role);
        // console.log("userDetails", userDetails);
        const { data: rolePermissions } = useGetRolePermissionsQuery(roleId || "", {
    skip: !roleId,
});

useEffect(() => {
    if (rolePermissions) {
        console.log("Role Permissions:", rolePermissions);
    }
}, [rolePermissions]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // opermission check helper
    const checkPermission = (permission: string, actionName: string) => {
        if (!isAdmin && roleName !== "Super Admin" && !can(permission)) {
            toast.error(`You don't have permission to ${actionName}`);
            return false;
        }
        
        return true;
    };

    // handlers with permission 
    const permissionNames = rolePermissions?.permissions?.map((perm: any) => perm.name) ?? [];
    const permissionDescriptions = rolePermissions?.permissions?.map(
        (permission: any) => permission.description
    );
    const hasPermission = (permission: string) => {
    return (
        permissionNames?.includes(permission) ||
        permissionDescriptions?.includes(permission)
    );
};

    const { handlePlanLimitError } = usePlanLimit();
    const { isFull: branchesFull } = usePlanUsage("branches");

    const handleAddBranch = async (data: any) => {
        if (!checkPermission("create branch", "add a branch")) return;

        if (!hasPermission("create branch") ) {
            toast.error("You don't have permission to add a branch");
            return;
        }
        
        try {
            await createBranch(data);
            await refetchBranches();
            setView("overview");
        } catch (err: any) {
            if (handlePlanLimitError(err)) return;
            toast.error(err?.data?.message || "Failed to add branch");
        }
    };
    

    const handleEditBranch = async (id: string, data: any) => {
        if (!checkPermission("update branch", "edit this branch")) return;
        if (!hasPermission("edit branch")) {
            toast.error("You don't have permission to edit this branch");
            return;
        }
        try {
            await updateBranch(id, data);
            await refetchBranches();
            toast.success("Branch updated successfully");
        } catch (err: any) {
            toast.error(err?.data?.message || "Failed to update branch");
        }
    };

    const handleDeleteBranch = async (id: string) => {
        if (!checkPermission("delete branch", "delete this branch")) return;
        if (!hasPermission("delete branch")) {
            toast.error("You don't have permission to delete this branch");
            return;
        }
        try {
            await deleteBranch(id);
            await refetchBranches();
            toast.success("Branch deleted successfully");
        } catch (err: any) {
            toast.error(err?.data?.message || "Failed to delete branch");
        }
    };

    const handleDeactivateBranch = async (id: string) => {
        if (!checkPermission("deactivate branch", "deactivate this branch")) return;
        
        if (!hasPermission("deactivate branch")) {
            toast.error("You don't have permission to deactivate this branch");
            return;
        }
        try {
            await deactivateBranch(id).unwrap();
            await refetchBranches();
            toast.success("Branch deactivated successfully");
        } catch (err: any) {
            toast.error(err?.data?.message || "Failed to deactivate branch");
        }
    };

    const handleReactivateBranch = async (id: string) => {
        if (!checkPermission("reactivate branch", "reactivate this branch")) return;
        
        if (!hasPermission("activate branch")) {
            toast.error("You don't have permission to reactivate this branch");
            return;
        }
        try {
            await reactivateBranch(id).unwrap();
            await refetchBranches();
            toast.success("Branch reactivated successfully");
        } catch (err: any) {
            toast.error(err?.data?.message || "Failed to reactivate branch");
        }
    };

    return (
        <>
            <DashboardLayout
                title="Branch"
                description="Track your branches, export data, and manage operations."
                containerClass="pb-10"
                dateRange={dateRange}
                onDateRangeChange={handleDateRangeChange}
                showDateRange
            >
                {isBranchesLoading ? (
                    <div className="flex items-center justify-center min-h-[400px]">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading Branches data...</p>
                    </div>
                    </div>
                ) : (
                <>
                {/* Add Branch Button */}
                {((isAdmin || roleName === "Super Admin") || can("create branch")) && (
                    <div className="flex flex-wrap justify-end items-center gap-3 mt-8">
                        <PlanUsage resource="branches" />
                        <div className="relative inline-block text-left">
                            <button
                                disabled={branchesFull}
                                title={branchesFull ? PLAN_FULL_TITLE : undefined}
                                onClick={() => {
                                    if (!hasPermission("create branch") ) {
                                        toast.error("You don't have permission to create branch");
                                        return;
                                    }
                                
                                    setDropdownOpen((prev) => !prev);
                                }}
                                className="rounded-lg bg-[#143887] text-white px-4 py-2 text-sm font-medium flex items-center gap-2 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <Plus className="h-4 w-4" />
                                Add New Branch <ChevronDown className="h-4 w-4" />
                            </button>

                            {dropdownOpen && (
                                <div
                                    ref={dropdownRef}
                                    className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-xl shadow-lg z-50"
                                >
                                    <button
                                        onClick={() => {
                                            setView("manualUpload");
                                            setDropdownOpen(false);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                                    >
                                        Manual Upload
                                    </button>

                                    <button
                                        onClick={() => {
                                            setShowBulkUploadModal(true);
                                            setDropdownOpen(false);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                                    >
                                        Bulk Upload
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <main className="mt-10 space-y-10">
                    {view === "overview" ? (
                        <>
                            {/* Summary Section */}
                            <section className="bg-[#E7E9EE] p-4 rounded-lg mt-10">
                                <h3 className="text-sm font-semibold text-[#00032A] mb-4">Summary</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <DashboardFleetSummaryCard
                                        title="Total Branches"
                                        value={totalBranches}
                                        changeText="Fleetrify Data"
                                        changeDirection="up"
                                    />
                                    <DashboardFleetSummaryCard
                                        title="Active Branches"
                                        value={activeCount}
                                        changeText="Fleetrify Data"
                                        changeDirection="up"
                                    />
                                    <DashboardFleetSummaryCard
                                        title="Deactivated Branches"
                                        value={inActiveCount}
                                        changeText="Fleetrify Data"
                                        changeDirection="down"
                                    />
                                </div>
                            </section>

                            {/* Branch Table */}
                            <FleetrifyCard cardContainerClass="mt-20">
                                <BranchTable
                                    tableData={branches}
                                    isLoading={isBranchesLoading}
                                    onEdit={handleEditBranch}
                                    onDelete={handleDeleteBranch}
                                    onDeactivate={handleDeactivateBranch}
                                    onReactivate={handleReactivateBranch}
                                    pageSize={pageSize}
                                    currentPage={currentPage}
                                    lastPage={totalPages}
                                    total={totalBranches}
                                    setCurrentPage={setCurrentPage}
                                    isAdmin={isAdmin}
                                    can={can}
                                />

                            </FleetrifyCard>
                        </>
                    ) : (
                        <BranchUpload onBack={() => setView("overview")} onSubmit={handleAddBranch} />
                    )}
                </main>
                </>)}
            </DashboardLayout>

            <BranchBulkUpload
                isModalOpen={showBulkUploadModal}
                setIsModalOpen={setShowBulkUploadModal}
            />
        </>
    );
};

export default Branch;
